import { NomadDB } from '~/db/NomadDB';

import { ContentTypes, ChildTypes } from '~/types/adamTypes';
import type { CategorySchema } from '~/types/schema';
import { arrayToObject } from '~/lib/utils/array';

import { getServices } from '~/db/services';

interface Ids {
  categoryIds: string[];
  postIds: string[];
  exerciseIds: string[];
}

export const populateIds = async (
  $db: NomadDB,
  ids: Ids,
  libraryBookId: string,
  populatePosts: boolean,
  populateExercises: boolean
): Promise<{
  mCategories: object;
  mPosts: object;
  mExercises: object;
}> => {
  const { categoryIds, postIds, exerciseIds } = ids;

  let categories: CategorySchema[];

  let posts: any[];

  let exercises: any[];

  const { categoriesService, postsService, exercisesService } =
    getServices($db);

  await $db.transaction(
    'r',
    $db.categories,
    $db.posts,
    $db.exercises,
    async () => {
      categories = await categoriesService.findByIdsAndLibraryBookId(
        categoryIds,
        libraryBookId
      );
      if (populatePosts) {
        posts = await postsService.findByIdsAndLibraryBookId(
          postIds,
          libraryBookId
        );
      } else {
        posts = postIds.map((item) => ({
          id: item,
        }));
      }
      if (populateExercises) {
        exercises = await exercisesService.findByIdsAndLibraryBookId(
          exerciseIds,
          libraryBookId
        );
      } else {
        exercises = exerciseIds.map((item) => ({
          id: item,
        }));
      }
    }
  );

  const mCategories = arrayToObject(categories);
  const mExercises = arrayToObject(exercises);
  const mPosts = arrayToObject(posts);

  return { mCategories, mPosts, mExercises };
};

export const sortChildren = (children): Ids => {
  const categoryIds = [];
  const postIds = [];
  const exerciseIds = [];
  children.forEach((child: any) => {
    switch (child.type) {
      case ChildTypes.Category:
        categoryIds.push(child.id);
        break;
      case ChildTypes.Post:
        postIds.push(child.id);
        break;
      case ChildTypes.Exercise:
        exerciseIds.push(child.id); // TODO Manage exercise type
        break;
      default:
        break;
    }
  });
  return { categoryIds, postIds, exerciseIds };
};

const getChildrenAtDepth = (
  data: any[],
  depth,
  currentDepth = 0,
  mainContentType = null,
  unlockDeepRootSection = false
) => {
  let children = [];

  const isRootCategory = [
    ContentTypes.CourseAndQuiz,
    ContentTypes.Training,
    ContentTypes.Testing,
    ContentTypes.Exam,
  ].includes(mainContentType);

  const accessSections = [
    ContentTypes.Chapter,
    ContentTypes.InterDiscipline,
    ContentTypes.InterDisciplineInfoIntox,
    ContentTypes.InterDisciplineDailyQuiz,
    ContentTypes.InterDisciplineActu,
    ContentTypes.InterDisciplineFocusOn,
  ];

  const unlockerSections = [
    ContentTypes.InterDisciplineInfoIntox,
    ContentTypes.InterDisciplineDailyQuiz,
  ];

  let unlockDeepRootSectionTemp: boolean = unlockDeepRootSection;
  data.forEach((parent: any) => {
    if (parent.children) {
      if (
        isRootCategory &&
        !unlockDeepRootSection &&
        currentDepth > 0 &&
        !accessSections.includes(parent.contentType) &&
        parent.children.length > 1
      ) {
        // This is a root category and not a contentTypes that need to be populated deeper so we stop populate children
        return;
      } else if (
        !unlockDeepRootSection &&
        isRootCategory &&
        unlockerSections.includes(parent.contentType)
      ) {
        unlockDeepRootSectionTemp = true;
      }

      children = [...children, ...parent.children];

      // }
    }
  });
  unlockDeepRootSection = unlockDeepRootSectionTemp;
  // globalThis.$logger.info('populate :: currentDepth', currentDepth, depth);
  if (currentDepth === depth) return children;
  return getChildrenAtDepth(
    children,
    depth,
    (currentDepth += 1),
    mainContentType,
    unlockDeepRootSection
  );
};

const getParentsAtDepth = (data: any[], depth, currentDepth = 0) => {
  if (depth === currentDepth) return data;
  let parents = [];
  data.forEach((parent: any) => {
    if (parent.children) parents = [...parents, ...parent.children];
  });
  return getParentsAtDepth(parents, depth, (currentDepth += 1));
};

const injectPopulated = (parents: any[], ids: any, displayHome: boolean) => {
  const { mCategories, mPosts, mExercises } = ids;
  // console.info('inject populated', mCategories, mPosts, mExercises);
  parents.forEach((parent) => {
    // globalThis.$logger.info('populate ::  parent', parent, parent?.children?.length);
    if (parent.children) {
      // globalThis.$logger.info('populate ::  children', [...parent.children]);
      const children = [...parent.children].reduce(
        (
          acc: {
            categories: any[];
            posts: any[];
            exercises: any[];
          },
          child: any
        ) => {
          // globalThis.$logger.info('populate :: type', child.type);
          switch (child.type) {
            case 'category':
              // globalThis.$logger.info(
              //   'populate :: id',
              //   child.id,
              //   mCategories[child.id]
              // );
              if (mCategories[child.id]) {
                if (displayHome === true) {
                  if (mCategories[child.id].displayHome === true) {
                    acc.categories.push(mCategories[child.id]);
                  }
                } else if (mCategories[child.id].displayHome !== true)
                  acc.categories.push(mCategories[child.id]);
              }

              break;
            case 'exercise':
              if (mExercises[child.id]) {
                acc.exercises.push(mExercises[child.id]);
              }
              break;
            case 'post':
              if (mPosts[child.id]) {
                acc.posts.push(mPosts[child.id]);
              }
              break;
          }
          return acc;
        },
        <{ categories: any[]; posts: any[]; exercises: any[] }>{
          categories: [],
          posts: [],
          exercises: [],
        }
      );
      // globalThis.$logger.info('populate ::  children.categories', children.categories);
      const updated =
        children.categories.length +
        children.posts.length +
        children.exercises.length;
      if (updated > 0) {
        parent.children = children.categories;
        parent.posts = children.posts;
        parent.exercises = children.exercises;
      }
    }
  });
};

const _populate = async (
  $db: NomadDB,
  data: any,
  libraryBookId: string,
  depth: number = 0,
  displayHome: boolean = false,
  unlockDeepRootSection: boolean = false,
  populatePosts: boolean = true,
  populateExercises: boolean = true
): Promise<{
  category: CategorySchema;
}> => {
  let childrenLength = 1;
  do {
    let lData;
    let contentType;

    if (!Array.isArray(data)) {
      lData = [data];
      contentType = data.contentType;
    } else {
      lData = data;
    }
    const children = getChildrenAtDepth(
      lData,
      depth,
      0,
      contentType,
      unlockDeepRootSection
    );
    childrenLength = children.length;
    const ids = sortChildren(children);
    const populatedIds = await populateIds(
      $db,
      ids,
      libraryBookId,
      populatePosts,
      populateExercises
    );
    const parents = getParentsAtDepth(lData, depth);
    injectPopulated(
      parents,
      populatedIds,
      (displayHome = depth === 0 ? displayHome : false)
    );
    depth++;
  } while (childrenLength > 0);

  return { category: data };
};

export const populate = async (
  $db: NomadDB,
  data,
  libraryBookId: string,
  displayHome: boolean = false,
  unlockDeepRootSection: boolean = false,
  populatePosts: boolean = true,
  populateExercises: boolean = true
): Promise<{
  category: CategorySchema;
}> => {
  const populated = await _populate(
    $db,
    data,
    libraryBookId,
    0,
    displayHome,
    unlockDeepRootSection,
    populatePosts,
    populateExercises
  );
  return populated;
};
